 "use client";

import { gdrtextlogo, herobgimage } from "@/lib/images";
import Image from "next/image";
import { useRef, useState } from "react";
import { ChevronRight, Volume2, VolumeX } from "lucide-react";

export default function HeroSectionGdrDevelopment() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMuted, setIsMuted] = useState(true);

    const toggleMute = () => {
        const nextMuted = !isMuted;
        setIsMuted(nextMuted);
        if (videoRef.current) {
            videoRef.current.muted = nextMuted;
        }
    };

    return (
        <section className="relative min-h-svh w-full overflow-hidden sm:min-h-0 sm:h-screen">
            {/* Background image (update src to your actual background) */}
            <Image
                src={herobgimage}
                alt="GDR Development background"
                fill
                priority
                className="object-cover h-full w-full opacity-80 z-0 relative"
            />
            {/* sm+: absolute logo above card (unchanged). Mobile logo is in-column above the reel — see below. */}
            <div className="pointer-events-none max-sm:hidden sm:contents">
                <Image
                    src={gdrtextlogo}
                    alt="GDR Development logo"
                    width={372}
                    height={67}
                    className="absolute left-1/2 top-40 z-20 h-[47px] w-[300px] -translate-x-1/2 md:h-[67px] md:w-[372px]"
                />
            </div>
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-[#000000A1]" />

            {/* Mobile: 9:16 reel · sm+: wide cinematic card */}
            <div className="relative z-10 flex min-h-svh w-full flex-col items-center justify-center px-3 pb-8 pt-[calc(9rem+env(safe-area-inset-top,0px))] shadow-[0px_4px_37.2px_18px_#73C0330F] sm:min-h-0 sm:h-full sm:px-0 sm:pb-0 sm:pt-60">
                {/* Mobile: logo sits above the reel, outside the video frame */}
                <div className="pointer-events-none mb-4 mt-1 shrink-0 sm:hidden">
                    <Image
                        src={gdrtextlogo}
                        alt="GDR Development logo"
                        width={372}
                        height={67}
                        className="mx-auto h-auto w-[260px]"
                    />
                </div>
                <div className="relative aspect-9/16 w-[min(88vw,360px)] max-h-[min(78dvh,680px)] shrink-0 overflow-hidden rounded-2xl border border-white/50 bg-black/40 sm:aspect-auto sm:h-[320px] sm:max-h-none sm:w-[500px] sm:rounded-[19px] md:h-[360px] md:w-[700px] lg:h-[447px] lg:w-[975px] 3xl:h-[600px] 3xl:w-[1200px]">
                    {/* Card background video */}
                    <video
                        ref={videoRef}
                        className="absolute inset-0 h-full w-full object-cover"
                        autoPlay
                        loop
                        muted={isMuted}
                        playsInline
                    >
                        <source src="/videos/Bg-Home-Main-Video.webm" type="video/webm" />
                    </video>

                    {/* Reel-style bottom fade (mobile) · uniform dim (sm+) */}
                    <div
                        className="absolute inset-0 bg-linear-to-t from-black/90 via-black/35 to-black/20 sm:hidden"
                        aria-hidden
                    />
                    <div className="absolute inset-0 hidden bg-black/40 sm:block" aria-hidden />

                    {/* Text + buttons */}
                    <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-6 pt-10 sm:flex-row sm:items-center sm:justify-start sm:px-10 sm:pb-0 sm:pt-0 md:px-14">
                        <div className="space-y-3 sm:space-y-0">
                            <h1
                                className="text-[26px] font-light leading-[1.1] tracking-tight text-white max-sm:max-w-[18ch] sm:text-[52px] sm:leading-tight md:text-[64px]"
                                style={{ fontFamily: "Canela Trial, serif" }}
                            >
                                <span className="block max-sm:whitespace-normal sm:whitespace-nowrap">
                                    REBUILT FOR A
                                </span>
                                <span className="block max-sm:whitespace-normal sm:whitespace-nowrap">
                                    HIGHER STANDARD
                                </span>
                            </h1>
                            <p className="text-sm font-normal leading-snug text-[#FFFFFFCC] sm:text-[20px] sm:leading-normal">
                                A limited collection of rebuilt historic homes led personally by
                                developer <span className="font-bold">Gus Renny</span>.
                            </p>

                            <div className="flex flex-wrap items-center gap-2.5 pt-1 sm:mt-5 sm:gap-3 sm:pt-0">
                                <button
                                    type="button"
                                    className="h-10 w-[140px] rounded-full border border-[#FFFFFF99] bg-black text-sm font-semibold text-white sm:h-[40px] sm:w-[150px] sm:text-base"
                                >
                                    Our Properties
                                </button>
                                <button
                                    type="button"
                                    className="relative inline-flex h-10 min-w-[140px] items-center gap-2 rounded-full bg-[linear-gradient(0deg,rgba(255,255,255,0.8)_0%,#FFFFFF_100%)] pl-4 pr-2 text-sm font-semibold text-black sm:h-[40px] sm:min-w-[150px] sm:pl-5 sm:text-base"
                                >
                                    <span className="whitespace-nowrap">Inquire now!</span>
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[radial-gradient(circle_at_30%_0%,#4B4B4B_0%,#111111_70%)] shadow-[0_3px_8px_rgba(0,0,0,0.5)]">
                                        <ChevronRight
                                            className="size-3.5 -translate-x-px text-white"
                                            strokeWidth={2.5}
                                            aria-hidden
                                        />
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={toggleMute}
                        aria-label={isMuted ? "Unmute video" : "Mute video"}
                        className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-[#FFFFFF33] bg-[#00000033] text-white backdrop-blur-md sm:bottom-5 sm:right-5 sm:top-auto sm:h-12 sm:w-12"
                    >
                        {isMuted ? (
                            <VolumeX className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
                        ) : (
                            <Volume2 className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
                        )}
                    </button>
                </div>
            </div>
        </section>
    );
}

